import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Fab,
  Collapse,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { generateResponse } from '../utils/reportProcessor';
import { getAIResponse } from '../utils/aiChatService';
import i18n from '../i18n';

const AidChatbot = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const language = i18n.language;
  const isRTL = language === 'ar';
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 1,
        text: t('chatbot.welcome'),
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  }, [t, messages.length]);

  // Update welcome message when language changes
  useEffect(() => {
    if (messages.length > 0) {
      setMessages(prev => prev.map((msg, index) => 
        index === 0 ? { ...msg, text: t('chatbot.welcome') } : msg
      ));
    }
  }, [language, t]);

  const predefinedQuestions = [
    { key: 'totalAid', text: t('chatbot.questions.totalAid') },
    { key: 'topDonors', text: t('chatbot.questions.topDonors') },
    { key: 'aidCategories', text: t('chatbot.questions.aidCategories') },
    { key: 'distributionIssues', text: t('chatbot.questions.distributionIssues') },
    { key: 'transparency', text: t('chatbot.questions.transparency') }
  ];

  const processUserQuery = async (query) => {
    setIsLoading(true);
    
    try {
      // Always try AI first, fallback to local processing if it fails
      const response = await getAIResponse(query, language);
      setIsLoading(false);
      return response;
    } catch (error) {
      console.error('Error processing query:', error);
      setIsLoading(false);
      return language === 'ar'
        ? 'عذراً، حدث خطأ في معالجة سؤالك. يرجى المحاولة مرة أخرى.'
        : 'Sorry, there was an error processing your question. Please try again.';
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    const response = await processUserQuery(inputValue);
    
    const botMessage = {
      id: Date.now() + 1,
      text: response,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);
  };

  const handleQuestionClick = async (questionText) => {
    const userMessage = {
      id: Date.now(),
      text: questionText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    const response = await processUserQuery(questionText);
    
    const botMessage = {
      id: Date.now() + 1,
      text: response,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: isRTL ? 'auto' : 24,
          left: isRTL ? 24 : 'auto',
          zIndex: 1000
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chatbot Window */}
      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: isRTL ? 'auto' : 24,
            left: isRTL ? 24 : 'auto',
            width: { xs: 'calc(100vw - 48px)', sm: 400 },
            height: 500,
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <BotIcon />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('chatbot.title')}
              
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 1,
              bgcolor: '#f5f5f5'
            }}
          >
            <List sx={{ p: 0 }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start'),
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      maxWidth: '80%',
                      flexDirection: message.sender === 'user' 
                        ? (isRTL ? 'row' : 'row-reverse') 
                        : (isRTL ? 'row-reverse' : 'row')
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main'
                      }}
                    >
                      {message.sender === 'user' ? <PersonIcon /> : <BotIcon />}
                    </Avatar>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: message.sender === 'user' ? 'white' : 'grey.100',
                        color: message.sender === 'user' ? 'text.primary' : 'text.primary',
                        borderRadius: 2,
                        maxWidth: '100%',
                        border: message.sender === 'user' ? '1px solid' : 'none',
                        borderColor: message.sender === 'user' ? 'primary.main' : 'transparent'
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.text}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexDirection: isRTL ? 'row-reverse' : 'row'
                  }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      <BotIcon />
                    </Avatar>
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                      <CircularProgress size={20} />
                    </Paper>
                  </Box>
                </ListItem>
              )}
            </List>
            <div ref={messagesEndRef} />
          </Box>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t('chatbot.suggestedQuestions')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {predefinedQuestions.map((question) => (
                    <Chip
                      key={question.key}
                      label={question.text}
                      variant="outlined"
                      size="small"
                      onClick={() => handleQuestionClick(question.text)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Input */}
          <Divider />
          <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('chatbot.placeholder')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  direction: isRTL ? 'rtl' : 'ltr'
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default AidChatbot;
